import { Stack } from "@mantine/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { spacing } from "#app/theme/v2/spacing";
import { SelectableCard } from "#components/molecules/SelectableCard/SelectableCard";
import { CompanySettingsPayload } from "#modules/company/infra/updateCompanySetting.api";
import { useCompany } from "#modules/company/view/useCompany";
import { useAddDefaultsMaterials } from "#modules/materials/view/useAddDefaultsMaterials";
import {
  getOnboardingMaterialMode,
  type OnboardingMaterialMode,
} from "#modules/onboardingV2/domain/onboardingSteps";
import { OnboardingV2Shell } from "#modules/onboardingV2/view/OnboardingV2Shell";
import { useTrackOnboardingStepViewed } from "#modules/onboardingV2/view/useOnboardingAnalytics";
import { useOnboardingStepNavigation } from "#modules/onboardingV2/view/useOnboardingStepNavigation";
import { useUpdateOnboardingMaterialMode } from "#modules/onboardingV2/view/useUpdateOnboardingMaterialMode";
import { MaterialVolumetricPricingStrategySchema, PricingModeSchema } from "shared";

const MATERIAL_MODE_OPTIONS: {
  value: OnboardingMaterialMode;
  translationKey: "rawFormat" | "realWeight" | "capableRectangle";
}[] = [
  { value: "RAW", translationKey: "rawFormat" },
  { value: "VOLUMETRIC_REAL_WEIGHT", translationKey: "realWeight" },
  { value: "VOLUMETRIC_CAPABLE_RECTANGLE", translationKey: "capableRectangle" },
];

const toCompanySettingsPayload = (value: OnboardingMaterialMode): CompanySettingsPayload => {
  if (value === "RAW") {
    return { materialPricingMode: PricingModeSchema.enum.RAW };
  }

  return {
    materialPricingMode: PricingModeSchema.enum.VOLUMETRIC,
    materialVolumetricPricingStrategy:
      value === "VOLUMETRIC_REAL_WEIGHT"
        ? MaterialVolumetricPricingStrategySchema.enum.REAL_WEIGHT
        : MaterialVolumetricPricingStrategySchema.enum.CAPABLE_RECTANGLE,
  };
};

export const OnboardingMaterialModeStepPage = () => {
  const { t } = useTranslation();
  const { company } = useCompany();
  const [selected, setSelected] = useState<OnboardingMaterialMode | null>(
    company ? getOnboardingMaterialMode(company.companySettings) : null,
  );
  const { position, total, goToNext, goToPrevious } = useOnboardingStepNavigation("materialMode", {
    materialMode: selected,
  });

  const { updateOnboardingMaterialMode, isPending: isUpdatingMaterialMode } =
    useUpdateOnboardingMaterialMode();
  const { addDefaultsMaterials, isPending: isAddingDefaultMaterials } = useAddDefaultsMaterials();
  const isContinuing = isUpdatingMaterialMode || isAddingDefaultMaterials;

  useTrackOnboardingStepViewed("materialMode");

  const handleContinue = async () => {
    if (selected === null || isContinuing) {
      return;
    }

    try {
      if (!company || selected !== getOnboardingMaterialMode(company.companySettings)) {
        await updateOnboardingMaterialMode(toCompanySettingsPayload(selected));
      }

      await addDefaultsMaterials();
      goToNext();
    } catch {
      return;
    }
  };

  return (
    <OnboardingV2Shell>
      <OnboardingV2Shell.Header
        onBack={goToPrevious}
        step={position}
        total={total}
        title={t("onboardingV2.materialMode.title")}
        subtitle={t("onboardingV2.materialMode.subtitle")}
      />

      <OnboardingV2Shell.Content>
        <Stack gap={spacing[4]} role="radiogroup" aria-label={t("onboardingV2.materialMode.title")}>
          {MATERIAL_MODE_OPTIONS.map((option) => (
            <SelectableCard
              key={option.value}
              variant="spacious"
              title={t(`onboardingV2.materialMode.options.${option.translationKey}.title`)}
              description={t(
                `onboardingV2.materialMode.options.${option.translationKey}.description`,
              )}
              selected={selected === option.value}
              onSelect={() => setSelected(option.value)}
            />
          ))}
        </Stack>
      </OnboardingV2Shell.Content>

      <OnboardingV2Shell.Footer
        buttonLabel={t("onboardingV2.continue")}
        onContinue={handleContinue}
        disabled={selected === null}
        loading={isContinuing}
        stepper={{ total, current: position }}
      />
    </OnboardingV2Shell>
  );
};
